import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { CardKinds } from './cardTemplateDbSlice';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type StandbyCardTemplateJoinedDb = {
  jointPaletteId: string;
  name: string;
  kind: CardKinds;
  author: string;
  standard: boolean;
}
const initial: StandbyCardTemplateJoinedDb = {
  jointPaletteId: '',
  name: '',
  kind: 'common',
  author: '',
  standard: false
};

export type StandbyCardTemplateJoinedDbHashType = {
  [key in string]: StandbyCardTemplateJoinedDb;
}

export type StandbyCardTemplateJoinedAddPayload = {
  id?: string;
} & StandbyCardTemplateJoinedDb;
export type StandbyCardTemplateJoinedUpdatePayload = {
  id: string;
} & { [P in keyof StandbyCardTemplateJoinedDb]?: StandbyCardTemplateJoinedDb[P] };

export type StandbyCardTemplateJoinedDbState = {
  standbyCardTemplateJoineds: StandbyCardTemplateJoinedDbHashType;
}

export const generateStandbyCardTemplateJoinedId = (): string => generateId('sctj');
let initialState: StandbyCardTemplateJoinedDbState = { standbyCardTemplateJoineds: {} };
const key = 'standbyCardTemplateJoineds';

const standbyCardTemplateJoinedDbSlice = createSlice({
  name: 'standbyCardTemplateJoinedDb',
  initialState,
  reducers: {
    initializeStandbyCardTemplateJoineds(state: StandbyCardTemplateJoinedDbState) {
      state.standbyCardTemplateJoineds = {};
    },
    addStandbyCardTemplateJoined(state: StandbyCardTemplateJoinedDbState, action: PayloadAction<StandbyCardTemplateJoinedAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateStandbyCardTemplateJoinedId();
      state.standbyCardTemplateJoineds[id] = obj;
      saveData(key, id, obj);
    },
    updateStandbyCardTemplateJoined(state: StandbyCardTemplateJoinedDbState, action: PayloadAction<StandbyCardTemplateJoinedUpdatePayload>) {
      const { id, ...obj } = action.payload;
      if (state.standbyCardTemplateJoineds[id] === undefined) {
        console.log(`*** StandbyCardTemplateJoined not found: ${id}`);
      } else {
        const newData = { ...state.standbyCardTemplateJoineds[id], ...obj };
        state.standbyCardTemplateJoineds[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteStandbyCardTemplateJoined(state: StandbyCardTemplateJoinedDbState, action: PayloadAction<StandbyCardTemplateJoinedUpdatePayload>) {
      const id = action.payload.id;
      delete state.standbyCardTemplateJoineds[id];
      deleteData(key, id);
    },
    saveStandbyCardTemplateJoineds(state: StandbyCardTemplateJoinedDbState, action: PayloadAction<void>) {
      saveAllData(key, state.standbyCardTemplateJoineds);
    },
    signUpStandbyCardTemplateJoineds(state: StandbyCardTemplateJoinedDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.standbyCardTemplateJoineds, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: StandbyCardTemplateJoinedDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.standbyCardTemplateJoineds = updatedValues;
      if (changed) saveAllData(key, state.standbyCardTemplateJoineds);
    })
  }
})

export const { initializeStandbyCardTemplateJoineds, addStandbyCardTemplateJoined, updateStandbyCardTemplateJoined, deleteStandbyCardTemplateJoined, saveStandbyCardTemplateJoineds, signUpStandbyCardTemplateJoineds } = standbyCardTemplateJoinedDbSlice.actions;
export default standbyCardTemplateJoinedDbSlice.reducer;
